
import withRouterParams from '../../../withRouterParams';
import request from '../../../request';
import swal from 'sweetalert';
import { showLoading, hideLoading } from '../../../loading';
import CenteredMessage from '../../../components/CenteredMessage';
import EntityInfo from '../EntityInfo';
import LabeledContainer from '../../../components/LabeledContainer';
import PillLabel from '../../../components/PillLabel';
import DateTime from '../../../components/DateTime';
import ResponsiveTable from '../../../components/ResponsiveTable';
import StyledLink from '../../../components/StyledLink';
import links from '../../../links';
import AttachmentList from '../../../components/AttachmentList';
import RoundedIconButton from '../../../components/RoundedIconButton';
import { Edit, FileDownload, Refresh, Visibility } from '@mui/icons-material';
import { ConnectEntityToQuickbooks } from '../../../components/ConnectEntityToQuickbooks';
import ConnectToQuickbooksButton from '../../../components/ConnectToQuickbooksButton';
import { formatDate, formatMoney, getAgentFullName, getCustomerFullName, hasIntersection } from '../../../../../shared/utils';
import { DOCUMENT_NAME, USER_ROLE } from '../../../../../shared/constants';
import Attachment from '../../../components/Attachment';
import { connect } from 'react-redux';
import { IconButton } from '@mui/material';
import { Link } from 'react-router';
import NavigationStateSaver from '../../../components/NavigationStateSaver';
import Editor from './Editor';
import PDF from '../../../PDF';


function Installations({ installations }) {
   return <LabeledContainer label="Installations" className="rounded-lg p-4">
      <ResponsiveTable
         cols={[
            {
               field: 'product',
               caption: 'Product',
               render: product => product ? product.name : '–'
            },
            {
               field: 'agent',
               caption: 'Agent',
               render: agent => agent ? <StyledLink to={links.agent(agent._id)}>
                     {getAgentFullName(agent)}
                  </StyledLink> : '–'
            },
            {
               field: 'Loan',
               caption: 'Loan',
               render: loan => loan ? <StyledLink to={links.loan(loan._id)}>{loan.loan_no}</StyledLink> : '–'
            },
            {
               field: 'status',
               caption: 'Status',
               render: status => <PillLabel>{status.toUpperCase()}</PillLabel>
            },
            {
               field: 'created_at',
               caption: 'Created At',
               render: created_at => <DateTime value={created_at} />
            },
            {
               field: 'actions',
               caption: '',
               render: (_, installation) => <>
                  <IconButton size='small' component={Link} to={links.installation(installation._id)}>
                     <Visibility fontSize='small' />
                  </IconButton>
               </>
            }
         ]}
         rows={installations}
      />
   </LabeledContainer>
}

function Loans({ loans }) {
   return <LabeledContainer label="Loans" className="rounded-lg p-4">
      <ResponsiveTable
         cols={[
            {
               field: 'loan_no',
               caption: 'Loan No',
               render: (loan_no, loan) => <StyledLink to={links.loan(loan._id)}>
                  {loan_no}
               </StyledLink>
            },
            {
               field: 'product',
               caption: 'Product',
               render: product => product ? product.name : '–'
            },
            {
               field: 'agent',
               caption: 'Agent',
               render: agent => agent ? <StyledLink to={links.agent(agent._id)}>
                     {getAgentFullName(agent)}
                  </StyledLink> : '–'
            },
            {
               field: 'price',
               caption: 'Price',
               render: price => formatMoney(price)
            },
            {
               field: 'status',
               caption: 'Status',
               render: status => <PillLabel>{status.toUpperCase()}</PillLabel>
            },
            {
               field: 'created_at',
               caption: 'Applied',
               render: created_at => <DateTime value={created_at} />
            }
         ]}
         rows={loans}
      />
   </LabeledContainer>
}

function Payments({ payments }) {

   return <LabeledContainer label="Payments" className="rounded-lg p-4">
      <ResponsiveTable

         cols={[
            {
               field: 'amount',
               caption: 'Amount',
               render: amount => formatMoney(amount)
            },
            {
               field: 'loan',
               caption: 'Loan',
               render: loan => loan ? <StyledLink to={links.loan(loan._id)}>
                  {loan.loan_no}
               </StyledLink> : '–'
            },
            {
               field: 'paid',
               caption: 'Paid',
               render: paid => paid ? <PillLabel className="bg-green-50 text-green-600">
                     YES
                  </PillLabel> : <PillLabel className="bg-red-50 text-red-600">
                     NO
                  </PillLabel>
            },
            {
               field: 'type',
               caption: 'Type',
               render: type => type ? <PillLabel className="uppercase">
                     {type}
                  </PillLabel> : '–'
            },
            {
               field: 'attachments',
               caption: 'Attachments',
               render: attachments => attachments.length ? <AttachmentList
                  attachments={attachments}
                  className="max-w-40"
               /> : '–'
            },
            {
               field: 'due_at',
               caption: 'Due At',
               render: created_at => <DateTime value={created_at} />
            }
         ]}
         rows={payments}
      />
   </LabeledContainer>
}


class _Customer extends NavigationStateSaver {

   state = {
      customer: null,
      connectToQuickbooksModalOpen: false,
      editorOpen: false
   }

   openEditor = () => {
      this.setState({ editorOpen: true });
   }

   closeEditor = async (data) => {
      
      if (data) {
         try {
            showLoading();

            const res = await request.patch(`/api/sales/customers/${this.getId()}`, data);
            this.setState({ customer: res.data });

            swal('Success', 'Customer updated successfully', 'success');

         } catch (err) {
            swal('Error', err.message, 'error');
            return;
         } finally {
            hideLoading();
         }
      }

      this.setState({ editorOpen: false });
   }

   openConnectToQuickbooksModal = () => {
      this.setState({ connectToQuickbooksModalOpen: true });
   }

   closeConnectToQuickbooksModal = (data) => {
      this.setState({ connectToQuickbooksModalOpen: false });

      if (data) {
         this.setState({
            customer: {
               ...this.state.customer,
               ...data
            }
         });
      }
   }
   
   getId() {
      return this.props.params.id;
   }

   fetchCustomer = async () => {

      try {
          
         showLoading();

         const res = await request.get(`/api/general/customers/${this.getId()}`);
         const customer = res.data;

         this.setState({ customer });

      } catch (err) {
         swal('Error', err.message, 'error');
      } finally {
         hideLoading();
      }
   }

   saveAsPdf = () => {
      const { customer } = this.state;
      const customerName = customer.is_company ? customer.name : `${customer.name} ${customer.surname}`;

      const pdf = new PDF(customerName);

      const personalInfo = {};
      if (!customer.is_company) {
         personalInfo['National ID'] = customer.national_id;
         personalInfo['Gender'] = customer.gender?.toUpperCase();
         personalInfo['Date of Birth'] = customer.dob ? formatDate(customer.dob) : '';
      }

      pdf.addHorizontalTable({
         title: 'Customer Details',
         data: {
            'Name': customerName,
            'Phone': customer.phone,
            'Email': customer.email,
            'Address': customer.address?.text,
            ...personalInfo,
            'Date Registered': formatDate(customer.created_at),
         }
      });

      // loans
      pdf.addSpace();
      
      pdf.addTable({
         title: 'Loans',
         headers: [ 'Loan No', 'Product', 'Sales Agent', 'Price', 'Status', 'Applied' ],
         rows: customer.loans.map(loan => [
            loan.loan_no,
            loan.product.name,
            loan.agent ? getAgentFullName(loan.agent) : '–',
            formatMoney(loan.price),
            loan.status.toUpperCase(),
            formatDate(loan.created_at)
         ])
      });
 
      // payments
      pdf.addSpace();

      pdf.addTable({
         title: 'Payments',
         headers: [ 'Loan', 'Amount', 'Paid', 'Type', 'Due Date' ],
         rows: customer.payments.map(payment => [
            payment.loan ? payment.loan.loan_no : '–',
            formatMoney(payment.amount),
            payment.paid ? 'YES' : 'NO',
            payment.type?.toUpperCase() || '–',
            formatDate(payment.due_at)
         ])
      });

      // save
      pdf.save();

   }

   async componentDidMount() {
      await super.componentDidMount();
      if (!this.state.customer)
         this.fetchCustomer();
   }

   render() {
      const { customer } = this.state;
      let content;

      const canUpdate = hasIntersection(this.props.roles, [ USER_ROLE.SUPER_ADMIN, USER_ROLE.SALES_MANAGER ]);

      if (!customer) {
         content = <CenteredMessage
            message="Customer data not loaded"
            onRefresh={this.fetchCustomer}
         />
      } else {
         content = <div className='p-4 flex flex-col gap-8 text-gray-600'>
            <div className='text-xl md:text-3xl font-bold truncate'>
               {getCustomerFullName(customer)}
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
               <EntityInfo
                  info={customer}
                  actions={
                     <IconButton
                        size='small'
                        onClick={this.openEditor}
                     >
                        <Edit fontSize='small' />
                     </IconButton>
                  }
               />
               <LabeledContainer label='Attachments' className='p-4 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {
                     Object.values(DOCUMENT_NAME.CUSTOMER).map(name => {
                        const attachment = customer.attachments.find(a => a.name === name);
                        const url = attachment ? attachment.url : null;
                        return <div key={name}>
                           <Attachment
                              name={name}
                              url={url}
                              onSave={
                                 async data => {
                                    const res = await request.post(`/api/sales/customers/${customer._id}/attachments`, data);
                                    this.setState({ customer: res.data });
                                 }
                              }
                              canEdit={canUpdate}
                           />
                        </div>
                     })
                  }
               </LabeledContainer>
            </div>

            <Loans loans={customer.loans} />
            <Installations installations={customer.installations} />

            <Payments
               payments={customer.payments}
            />

         </div>
      }

      return <div className='h-full grid grid-rows-[auto,1fr]'>
         <div className='overflow-y-auto'>
            {content}
         </div>
         <div className='flex flex-row items-center justify-end gap-4 px-4 py-2 border-t'>
            {
               customer && !Boolean(customer.quickbooks_id) && <ConnectToQuickbooksButton
                  size='small'
                  onClick={this.openConnectToQuickbooksModal}
               />
            }
            <RoundedIconButton color="secondary" size='small' onClick={this.fetchCustomer}>
               <Refresh />
            </RoundedIconButton>
            <RoundedIconButton color="primary" size='small' onClick={this.saveAsPdf}>
               <FileDownload />
            </RoundedIconButton>
         </div>
         {
            this.state.connectToQuickbooksModalOpen && <ConnectEntityToQuickbooks
               quickbooksIdKey="quickbooks_id"
               submitUrl={"/api/general/quickbooks/customer-connection"}
               fetchData={async () => {
                  const res = await request.get("/api/general/quickbooks/customers");
                  return res.data.customers;
               }}
               entityKey="customer"
               entityId={this.getId()}
               renderOption={(props, option) => {
                  return <li {...props} className="border-b last:border-none cursor-pointer hover:bg-gray-100">
                     <div className='px-4 py-2'>
                        {option.name}
                     </div>
                  </li>
               }}
               onClose={this.closeConnectToQuickbooksModal}
            />
         }
         {
            this.state.editorOpen && <Editor
               customer={customer}
               onClose={this.closeEditor}
            />
         }
      </div>
   }
}

const mapStateToProps = state => ({ roles: state.user.roles });
const Customer = connect(mapStateToProps)(withRouterParams(_Customer));

export default Customer;

